/**
 * PoliciesPage - Credit policy configuration
 * Tabs: Elegibilidad, Niveles, Scoring, Tasas, Limites de Exposicion
 * (Split moved to Operaciones > Split, History is its own page)
 */

import { useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs } from '@/components/ui/Tabs';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks';
import { ROUTES, PERMISSIONS } from '@/constants';
import { usePolicies } from '../../hooks';
import {
  EligibilitySection,
  CreditLevelsSection,
  ScoresSection,
  InterestRatesSection,
  ExposureLimitsSection,
  WhitelistSection,
} from '../../components';

// Order follows the credit engine pipeline: filter → score → classify → price → limit
const POLICY_TABS: Array<{ key: string; label: string; helpKey?: string }> = [
  { key: 'eligibility', label: 'Elegibilidad', helpKey: 'policies-eligibility' },
  { key: 'scores', label: 'Scoring', helpKey: 'policies-scoring' },
  { key: 'credit-levels', label: 'Niveles de Credito', helpKey: 'policies-credit-levels' },
  { key: 'interest-rates', label: 'Tasas de Interes', helpKey: 'policies-interest-rates' },
  { key: 'exposure-limits', label: 'Limites de Exposicion', helpKey: 'policies-exposure-limits' },
  { key: 'whitelist', label: 'Whitelist', helpKey: 'policies-whitelist' },
];

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Configuracion', path: ROUTES.POLICIES },
  { label: 'Politicas', path: ROUTES.POLICIES },
];

export function PoliciesPage() {
  const [activeTab, setActiveTab] = useState<string>('eligibility');
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PERMISSIONS.CREDIT_POLICIES_EDIT);

  const {
    eligibility,
    creditLevels,
    scores,
    interestRates,
    openingFee,
    exposureLimits,
    updateEligibility,
    updateCreditLevels,
    updateScores,
    updateInterestRates,
    updateOpeningFee,
    updateExposureLimits,
    isSavingEligibility,
    isSavingCreditLevels,
    isSavingScores,
    isSavingInterestRates,
    isSavingOpeningFee,
    isSavingExposureLimits,
    isLoading,
    isError,
    refetchAll,
  } = usePolicies();

  function renderActiveSection() {
    switch (activeTab) {
      case 'eligibility':
        return (
          <EligibilitySection
            data={eligibility}
            canEdit={canEdit}
            onSave={updateEligibility}
            isSaving={isSavingEligibility}
          />
        );
      case 'credit-levels':
        return (
          <CreditLevelsSection
            data={creditLevels}
            canEdit={canEdit}
            onSave={updateCreditLevels}
            isSaving={isSavingCreditLevels}
          />
        );
      case 'scores':
        return (
          <ScoresSection
            data={scores as any}
            canEdit={canEdit}
            onSave={(items: any) => {
              if (updateScores) updateScores(items);
            }}
            isSaving={isSavingScores}
          />
        );
      case 'interest-rates':
        return (
          <InterestRatesSection
            data={interestRates}
            feeData={openingFee}
            canEdit={canEdit}
            onSave={updateInterestRates}
            onSaveFee={updateOpeningFee}
            isSaving={isSavingInterestRates}
            isSavingFee={isSavingOpeningFee}
          />
        );
      case 'exposure-limits':
        return (
          <ExposureLimitsSection
            data={exposureLimits}
            canEdit={canEdit}
            onSave={updateExposureLimits}
            isSaving={isSavingExposureLimits}
          />
        );
      case 'whitelist':
        return <WhitelistSection />;
      default:
        return null;
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Politicas de Credito"
        subtitle="Configuracion del motor de credito"
        breadcrumbs={BREADCRUMBS}
        helpKey="policies"
      />

      <Tabs
        tabs={POLICY_TABS}
        activeTab={activeTab}
        onChange={(key) => setActiveTab(key)}
      />

      {isLoading && (
        <div className="space-y-4">
          <Skeleton width="100%" height={40} variant="rectangular" />
          <Skeleton width="100%" height={200} variant="rectangular" />
          <Skeleton width="100%" height={200} variant="rectangular" />
        </div>
      )}

      {isError && !isLoading && (
        <div className="flex flex-col items-center gap-4 rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center">
          <p className="text-sm text-destructive">
            Error al cargar las politicas. Intenta de nuevo.
          </p>
          <Button variant="outline" size="sm" onClick={refetchAll}>
            Reintentar
          </Button>
        </div>
      )}

      {!isLoading && !isError && (
        <div>{renderActiveSection()}</div>
      )}
    </div>
  );
}
